import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AssetPreview } from "../../components/AssetPreview";
import { useTenantAuth } from "../../context/auth";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api";

type Service = {
  id: string;
  name: string;
  duration_minutes: number;
  price_cents: number;
  branch_id: string;
  is_active: boolean;
  photo_asset_id?: string | null;
};

type UploadOut = { id: string };
type Asset = { id: string; title?: string | null; original_filename: string };

export function ManagerServicesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { bootstrap, token } = useTenantAuth();
  const [branchId, setBranchId] = useState(bootstrap?.branches[0]?.id ?? "");
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(30);
  const [price, setPrice] = useState(2500);
  const [photoAssetId, setPhotoAssetId] = useState("");

  const [editingId, setEditingId] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  const [editName, setEditName] = useState("");
  const [editDuration, setEditDuration] = useState(30);
  const [editPrice, setEditPrice] = useState(0);
  const [editActive, setEditActive] = useState(true);
  const [editPhotoAssetId, setEditPhotoAssetId] = useState("");

  const [uploadingFor, setUploadingFor] = useState("");

  const list = useQuery({
    queryKey: ["manager-services"],
    queryFn: () => apiGet<{ items: Service[] }>("/v3/services"),
  });
  const assets = useQuery({
    queryKey: ["manager-service-assets"],
    queryFn: () => apiGet<{ items: Asset[] }>("/v3/branding/assets?deleted=false&type=service"),
  });

  async function uploadServicePhoto(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "service");
    const res = await fetch("/v3/branding/assets", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) {
      throw new Error("Asset upload failed");
    }
    const data = (await res.json()) as UploadOut;
    await qc.invalidateQueries({ queryKey: ["manager-service-assets"] });
    return data.id;
  }

  const create = useMutation({
    mutationFn: () =>
      apiPost("/v3/services", {
        branch_id: branchId,
        name,
        duration_minutes: duration,
        price_cents: price,
        photo_asset_id: photoAssetId || null,
        is_active: true,
      }),
    onSuccess: async () => {
      setName("");
      setPhotoAssetId("");
      await qc.invalidateQueries({ queryKey: ["manager-services"] });
    },
  });

  const update = useMutation({
    mutationFn: () =>
      apiPut(`/v3/services/${editingId}`, {
        branch_id: editBranchId,
        name: editName,
        duration_minutes: editDuration,
        price_cents: editPrice,
        photo_asset_id: editPhotoAssetId || null,
        is_active: editActive,
      }),
    onSuccess: async () => {
      setEditingId("");
      await qc.invalidateQueries({ queryKey: ["manager-services"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/v3/services/${id}`),
    onSuccess: async () => {
      if (editingId) setEditingId("");
      await qc.invalidateQueries({ queryKey: ["manager-services"] });
    },
  });

  const beginEdit = (row: Service) => {
    setEditingId(row.id);
    setEditBranchId(row.branch_id);
    setEditName(row.name);
    setEditDuration(row.duration_minutes);
    setEditPrice(row.price_cents);
    setEditActive(row.is_active);
    setEditPhotoAssetId(row.photo_asset_id || "");
  };

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">{t("manager.services.title")}</h1>
      <section className="panel p-4">
        <div className="grid gap-2">
          {(list.data?.items ?? []).map((row) => (
            <article key={row.id} className="rounded-lg border border-slate-200 p-3 bg-white">
              {editingId === row.id ? (
                <div className="grid gap-2">
                  <select className="input" value={editBranchId} onChange={(e) => setEditBranchId(e.target.value)}>
                    {(bootstrap?.branches ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                  <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input className="input" type="number" min={15} step={15} value={editDuration} onChange={(e) => setEditDuration(Number(e.target.value || "0"))} />
                    <input className="input" type="number" min={0} step={100} value={editPrice} onChange={(e) => setEditPrice(Number(e.target.value || "0"))} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {editPhotoAssetId ? <AssetPreview assetId={editPhotoAssetId} token={token} className="h-12 w-12 rounded object-cover border" alt={t("services")} /> : null}
                    <select className="input min-w-0 flex-1" value={editPhotoAssetId} onChange={(e) => setEditPhotoAssetId(e.target.value)}>
                      <option value="">{t("manager.media.selectFromLibrary")}</option>
                      {(assets.data?.items ?? []).map((asset) => (
                        <option key={asset.id} value={asset.id}>
                          {asset.title || asset.original_filename}
                        </option>
                      ))}
                    </select>
                      <input
                        className="input w-full sm:w-auto"
                        type="file"
                        accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingFor(row.id);
                        try {
                          const nextId = await uploadServicePhoto(file);
                          setEditPhotoAssetId(nextId);
                        } finally {
                          setUploadingFor("");
                        }
                      }}
                    />
                    {editPhotoAssetId ? (
                      <button className="btn secondary" onClick={() => setEditPhotoAssetId("")}>
                        {t("manager.media.removePhoto")}
                      </button>
                    ) : null}
                  </div>
                  <label className="text-sm">
                    <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} /> {t("common.active")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button className="btn" onClick={() => update.mutate()} disabled={!editName.trim() || !editBranchId || uploadingFor === row.id}>
                      Save
                    </button>
                    <button className="btn secondary" onClick={() => setEditingId("")}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between gap-2">
                    <p className="font-semibold">{row.name}</p>
                    <p className="text-sm text-slate-600">${(row.price_cents / 100).toFixed(2)}</p>
                  </div>
                  <p className="text-xs text-slate-500">{row.duration_minutes} min</p>
                  {row.photo_asset_id ? (
                    <AssetPreview assetId={row.photo_asset_id} token={token} className="mt-2 h-14 w-14 rounded object-cover border" alt={row.name} />
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button className="btn secondary" onClick={() => beginEdit(row)}>
                      Edit
                    </button>
                    <button className="btn secondary" onClick={() => remove.mutate(row.id)}>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </article>
          ))}
        </div>
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.services.addService")}</h2>
        <div className="grid gap-2 mt-2 sm:grid-cols-2 lg:grid-cols-4">
          <select className="input" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {(bootstrap?.branches ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <input className="input" placeholder={t("manager.services.placeholder.serviceName")} value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" type="number" min={15} step={15} value={duration} onChange={(e) => setDuration(Number(e.target.value || "0"))} />
          <input className="input" type="number" min={0} step={100} value={price} onChange={(e) => setPrice(Number(e.target.value || "0"))} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {photoAssetId ? <AssetPreview assetId={photoAssetId} token={token} className="h-12 w-12 rounded object-cover border" alt={t("services")} /> : null}
          <select className="input min-w-0 flex-1" value={photoAssetId} onChange={(e) => setPhotoAssetId(e.target.value)}>
            <option value="">{t("manager.media.selectFromLibrary")}</option>
            {(assets.data?.items ?? []).map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.title || asset.original_filename}
              </option>
            ))}
          </select>
          <input
            className="input w-full sm:w-auto"
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setUploadingFor("create");
              try {
                const nextId = await uploadServicePhoto(file);
                setPhotoAssetId(nextId);
              } finally {
                setUploadingFor("");
              }
            }}
          />
          {photoAssetId ? (
            <button className="btn secondary" onClick={() => setPhotoAssetId("")}>
              {t("manager.media.removePhoto")}
            </button>
          ) : null}
        </div>
        <button className="btn mt-2 w-full sm:w-auto" onClick={() => create.mutate()} disabled={!name.trim() || !branchId || uploadingFor === "create"}>
          {t("manager.services.action.createService")}
        </button>
      </section>
    </div>
  );
}



